
import { useState } from 'react';
import { SEO } from '../../../utils/seo';
import GlobalNavbar from '../../../components/feature/GlobalNavbar';
import Footer from '../../../components/feature/Footer';

const HeirshipContactForm = ({
  formId,
  title,
  subtitle,
}: {
  formId: string;
  title: string;
  subtitle: string;
}) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    propertyAddress: '',
    situation: '',
    timeline: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('propertyAddress', formData.propertyAddress);
      formBody.append('situation', formData.situation);
      formBody.append('timeline', formData.timeline);
      formBody.append('message', formData.message);

      const response = await fetch(
        'https://readdy.ai/api/form/d67ao2ea728k8ctu1pt0',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: formBody.toString(),
        }
      );

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(
          `Server responded with ${response.status}: ${errText}`
        );
      }

      setIsSubmitted(true);
    } catch (error) {
      console.error('Form submission error:', error);
      setErrorMessage(
        'Something went wrong while submitting the form. Please try again later.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="bg-teal-50 border border-teal-200 rounded-xl p-8 text-center">
        <div className="w-16 h-16 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-4">
          <i className="ri-check-line text-3xl text-teal-600"></i>
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h3>
        <p className="text-gray-600">
          We&apos;ve received your information and will contact you within 24
          hours to discuss your heirship property situation.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-xl p-8 border border-gray-100">
      <div className="text-center mb-6">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">{title}</h3>
        <p className="text-gray-600">{subtitle}</p>
      </div>

      <form
        id={formId}
        data-readdy-form
        onSubmit={handleSubmit}
        className="space-y-4"
      >
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Full Name *
            </label>
            <input
              type="text"
              name="name"
              required
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              placeholder="Your full name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone Number *
            </label>
            <input
              type="tel"
              name="phone"
              required
              value={formData.phone}
              onChange={(e) =>
                setFormData({ ...formData, phone: e.target.value })
              }
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              placeholder="(915) 555-0123"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email Address *
          </label>
          <input
            type="email"
            name="email"
            required
            value={formData.email}
            onChange={(e) =>
              setFormData({ ...formData, email: e.target.value })
            }
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            placeholder="your@email.com"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Property Address
          </label>
          <input
            type="text"
            name="propertyAddress"
            value={formData.propertyAddress}
            onChange={(e) =>
              setFormData({ ...formData, propertyAddress: e.target.value })
            }
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            placeholder="Address of the inherited property"
          />
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Your Situation
            </label>
            <select
              name="situation"
              value={formData.situation}
              onChange={(e) =>
                setFormData({ ...formData, situation: e.target.value })
              }
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
            >
              <option value="">Select situation</option>
              <option value="Need Affidavit of Heirship">
                Need an Affidavit of Heirship
              </option>
              <option value="Property Stuck in Deceased Name">
                Property stuck in deceased&apos;s name
              </option>
              <option value="No Will Exists">No will exists</option>
              <option value="Multiple Heirs Involved">Multiple heirs involved</option>
              <option value="Want to Sell Inherited Property">
                Want to sell inherited property
              </option>
              <option value="Title Company Requires Documentation">
                Title company requires documentation
              </option>
              <option value="Probate Too Expensive">Probate is too expensive</option>
              <option value="Other">Other situation</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Timeline
            </label>
            <select
              name="timeline"
              value={formData.timeline}
              onChange={(e) =>
                setFormData({ ...formData, timeline: e.target.value })
              }
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
            >
              <option value="">Select timeline</option>
              <option value="ASAP">Need to sell ASAP</option>
              <option value="1-3 months">Within 1-3 months</option>
              <option value="3-6 months">Within 3-6 months</option>
              <option value="6+ months">6+ months</option>
              <option value="Just exploring">Just exploring options</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Tell Us More
          </label>
          <textarea
            name="message"
            value={formData.message}
            onChange={(e) =>
              setFormData({
                ...formData,
                message: e.target.value.slice(0, 500),
              })
            }
            rows={3}
            maxLength={500}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
            placeholder="Tell us about your situation and how we can help..."
          />
          <p className="text-xs text-gray-500 mt-1">
            {formData.message.length}/500 characters
          </p>
        </div>

        {errorMessage && (
          <p className="text-sm text-red-600">{errorMessage}</p>
        )}

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full py-4 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center">
              <i className="ri-loader-4-line animate-spin mr-2"></i>
              Submitting...
            </span>
          ) : (
            'Get Your Free Consultation'
          )}
        </button>

        <p className="text-xs text-gray-500 text-center">
          Your information is secure and will never be sold. We&apos;re here to
          help.
        </p>
      </form>
    </div>
  );
};

export default function AffidavitOfHeirshipPage() {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Affidavit of Heirship in Texas: Complete Guide for El Paso Homeowners | TREX RE LLC"
        description="Learn everything about Affidavits of Heirship in Texas. Understand when you need one, how to get one prepared, requirements, costs, and how it helps transfer inherited property without probate in El Paso."
        keywords="affidavit of heirship Texas, affidavit of heirship El Paso, transfer property without probate, inherited property Texas, heirship affidavit requirements, Texas estate documents, El Paso inherited homes"
        canonical="/elpaso-tx/affidavit-of-heirship"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline:
            'Affidavit of Heirship in Texas: Complete Guide for El Paso Homeowners',
          description:
            'Comprehensive guide to understanding and obtaining an Affidavit of Heirship in Texas, including requirements, costs, and how it helps transfer inherited property without probate.',
          author: {
            '@type': 'Organization',
            name: 'TREX RE LLC',
          },
        }}
      />
      <GlobalNavbar />

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(https://public.readdy.ai/ai/img_res/92e2bf83add54991306e540fcd89a048.jpg)`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-20 w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-4 py-2 bg-teal-500/90 text-white text-sm font-semibold rounded-full mb-6">
                <i className="ri-file-text-line mr-2"></i>
                Estate Documentation Guide
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                Affidavit of Heirship:{' '}
                <span className="text-teal-400">Your Complete Guide</span>
              </h1>
              <p className="text-xl text-gray-200 mb-8 leading-relaxed">
                Need help transferring inherited property without going through
                expensive probate? Learn how an Affidavit of Heirship can help
                El Paso families establish ownership and sell inherited real
                estate.
              </p>

              <div className="flex flex-wrap gap-4 mb-8">
                <div className="flex items-center text-white">
                  <i className="ri-checkbox-circle-line text-teal-400 mr-2"></i>
                  <span>Avoid Costly Probate</span>
                </div>
                <div className="flex items-center text-white">
                  <i className="ri-checkbox-circle-line text-teal-400 mr-2"></i>
                  <span>Establish Clear Title</span>
                </div>
                <div className="flex items-center text-white">
                  <i className="ri-checkbox-circle-line text-teal-400 mr-2"></i>
                  <span>Sell Faster</span>
                </div>
              </div>
            </div>

            <div>
              <HeirshipContactForm
                formId="hero-heirship-form"
                title="Need Help With an Affidavit of Heirship?"
                subtitle="We help families navigate inherited property"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <article className="max-w-4xl mx-auto px-6 py-16">
        {/* Introduction */}
        <div className="prose prose-lg max-w-none mb-12">
          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            If you&apos;ve inherited property in Texas and the deceased owner
            didn&apos;t leave a will — or if probate was never opened — you may
            be wondering how to legally establish your ownership. The property
            is still titled in your loved one&apos;s name, and you can&apos;t
            sell it, refinance it, or even get clear title insurance without
            proving you&apos;re the rightful heir. This is where an{' '}
            <strong>Affidavit of Heirship</strong> becomes invaluable.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            An Affidavit of Heirship is a legal document that establishes who
            the rightful heirs are when someone dies without a will (intestate)
            or when formal probate proceedings were never initiated. It&apos;s
            one of the most practical tools available to Texas families
            dealing with inherited real estate, and it can save thousands of
            dollars compared to formal probate.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-8">
            In this comprehensive guide, we&apos;ll explain exactly what an
            Affidavit of Heirship is, when you need one, how to get one
            prepared, what it costs, and how it can help you finally transfer or
            sell that inherited property. Whether you&apos;re dealing with a
            recent death or property that&apos;s been stuck in a deceased
            relative&apos;s name for years, this guide will give you the
            knowledge you need to move forward.
          </p>
        </div>

        {/* What Is an Affidavit of Heirship Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            What Exactly Is an Affidavit of Heirship?
          </h2>

          <img
            src="https://readdy.ai/api/search-image?query=legal%20document%20with%20affidavit%20title%20notary%20seal%20and%20signatures%20on%20professional%20desk%20with%20family%20photographs%20in%20background%20showing%20inheritance%20and%20estate%20documentation%20warm%20natural%20lighting%20professional%20photography&width=800&height=500&seq=heirship-doc-002&orientation=landscape"
            alt="Affidavit of Heirship legal document"
            className="w-full h-96 object-cover rounded-lg shadow-lg mb-8"
          />

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            An Affidavit of Heirship is a sworn legal document that identifies
            the heirs of a deceased person and establishes their right to
            inherit property. Unlike probate, which is a court-supervised
            process, an Affidavit of Heirship is a private document that gets
            recorded in the county property records where the real estate is
            located.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Think of it as a detailed family history statement that creates a
            paper trail for title companies and future buyers. The affidavit
            describes the deceased person&apos;s family relationships,
            identifies all potential heirs under Texas intestacy laws, and
            declares who is entitled to inherit the property.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Here&apos;s what makes an Affidavit of Heirship unique:{' '}
            <strong>it must be signed by someone who is NOT an heir</strong>.
            This requirement exists to ensure objectivity. The person signing
            (called the &quot;affiant&quot;) must have personal knowledge of the
            deceased&apos;s family history but cannot benefit from the
            inheritance. Typically, this is a longtime friend, neighbor, or
            extended family member who knew the deceased well.
          </p>

          <div className="bg-teal-50 border-l-4 border-teal-600 p-6 rounded-lg mb-6">
            <h4 className="text-xl font-bold text-gray-900 mb-3">
              <i className="ri-lightbulb-line mr-2 text-teal-600"></i>
              Key Point
            </h4>
            <p className="text-gray-700">
              An Affidavit of Heirship is NOT a court order. It doesn&apos;t
              have the same legal weight as a probate decree. However, when
              properly prepared and recorded, it creates sufficient evidence of
              heirship for most title companies to insure the property —
              which is what you need to sell or transfer it.
            </p>
          </div>
        </section>

        {/* When Do You Need One Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            When Do You Need an Affidavit of Heirship?
          </h2>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            An Affidavit of Heirship is most commonly used in the following
            situations:
          </p>

          <div className="space-y-6 mb-8">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="text-xl font-bold text-gray-900 mb-3">
                <i className="ri-file-unknow-line text-teal-600 mr-2"></i>
                1. The Deceased Died Without a Will (Intestate)
              </h4>
              <p className="text-gray-700">
                When someone dies without a will, Texas intestacy laws determine
                who inherits their property. An Affidavit of Heirship documents
                these legal heirs and creates a record that can be used to
                transfer property. This is the most common scenario where
                heirship affidavits are used.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="text-xl font-bold text-gray-900 mb-3">
                <i className="ri-time-line text-teal-600 mr-2"></i>
                2. Probate Was Never Opened
              </h4>
              <p className="text-gray-700">
                Sometimes families simply never get around to probating a will or
                opening an estate. Years pass, and suddenly someone wants to sell
                the property. If the original owner died long ago and no probate
                was ever filed, an Affidavit of Heirship can help establish the
                chain of title. If you&apos;re wondering about the{' '}
                <a
                  href="/elpaso-tx/what-happens-if-you-dont-probate-will-texas"
                  className="text-teal-600 hover:text-teal-700 font-medium"
                >
                  consequences of not probating a will in Texas
                </a>
                , an Affidavit of Heirship is often the solution.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="text-xl font-bold text-gray-900 mb-3">
                <i className="ri-calendar-close-line text-teal-600 mr-2"></i>
                3. The Four-Year Probate Deadline Has Passed
              </h4>
              <p className="text-gray-700">
                In Texas, a will generally must be probated within four years of
                death. If this deadline has passed, probating the will becomes
                much more difficult. An Affidavit of Heirship provides an
                alternative path to establishing ownership when formal probate is
                no longer a practical option.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="text-xl font-bold text-gray-900 mb-3">
                <i className="ri-money-dollar-circle-line text-teal-600 mr-2"></i>
                4. Probate Is Too Expensive for the Estate Value
              </h4>
              <p className="text-gray-700">
                For smaller estates, the cost of formal probate may exceed the
                value of the property. An Affidavit of Heirship costs a fraction
                of what probate costs, making it a practical alternative when
                the estate doesn&apos;t justify expensive legal proceedings.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="text-xl font-bold text-gray-900 mb-3">
                <i className="ri-home-line text-teal-600 mr-2"></i>
                5. You Need to Sell Inherited Property Quickly
              </h4>
              <p className="text-gray-700">
                If you need to sell inherited property and time is of the essence,
                an Affidavit of Heirship can be prepared and recorded much faster
                than going through probate. This is especially valuable when
                heirs need to{' '}
                <a
                  href="/rental-property"
                  className="text-teal-600 hover:text-teal-700 font-medium"
                >
                  sell rental properties with existing tenants
                </a>{' '}
                or deal with properties that are becoming financial burdens.
              </p>
            </div>
          </div>

          <img
            src="https://readdy.ai/api/search-image?query=multigenerational%20family%20looking%20at%20old%20photographs%20and%20documents%20together%20discussing%20family%20history%20and%20inheritance%20warm%20living%20room%20setting%20natural%20lighting%20showing%20emotional%20family%20moment%20professional%20photography&width=800&height=500&seq=heirship-family-003&orientation=landscape"
            alt="Family reviewing inheritance documents together"
            className="w-full h-96 object-cover rounded-lg shadow-lg mb-8"
          />
        </section>

        {/* What Information Is Required Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            What Information Is Required in an Affidavit of Heirship?
          </h2>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            A properly prepared Affidavit of Heirship must contain specific
            information to be effective. Here&apos;s what&apos;s typically
            required:
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
              <h4 className="font-bold text-gray-900 mb-3 flex items-center">
                <i className="ri-user-line text-teal-600 mr-2"></i>
                About the Deceased (Decedent)
              </h4>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-line text-teal-600 mr-2 mt-0.5"></i>
                  Full legal name and any aliases
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-line text-teal-600 mr-2 mt-0.5"></i>
                  Date and place of birth
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-line text-teal-600 mr-2 mt-0.5"></i>
                  Date and place of death
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-line text-teal-600 mr-2 mt-0.5"></i>
                  Last residence address
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-line text-teal-600 mr-2 mt-0.5"></i>
                  Social Security Number (if available)
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-line text-teal-600 mr-2 mt-0.5"></i>
                  Marital history (all marriages)
                </li>
              </ul>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
              <h4 className="font-bold text-gray-900 mb-3 flex items-center">
                <i className="ri-group-line text-teal-600 mr-2"></i>
                About the Heirs
              </h4>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-line text-teal-600 mr-2 mt-0.5"></i>
                  Names of all children (including deceased children)
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-line text-teal-600 mr-2 mt-0.5"></i>
                  Surviving spouse information
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-line text-teal-600 mr-2 mt-0.5"></i>
                  Parents&apos; names (if relevant)
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-line text-teal-600 mr-2 mt-0.5"></i>
                  Siblings&apos; names (if relevant)
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-line text-teal-600 mr-2 mt-0.5"></i>
                  Each heir&apos;s relationship to deceased
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-line text-teal-600 mr-2 mt-0.5"></i>
                  Each heir&apos;s fractional interest
                </li>
              </ul>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
              <h4 className="font-bold text-gray-900 mb-3 flex items-center">
                <i className="ri-home-gear-line text-teal-600 mr-2"></i>
                About the Property
              </h4>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-line text-teal-600 mr-2 mt-0.5"></i>
                  Full legal description of property
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-line text-teal-600 mr-2 mt-0.5"></i>
                  Street address
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-line text-teal-600 mr-2 mt-0.5"></i>
                  County where property is located
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-line text-teal-600 mr-2 mt-0.5"></i>
                  How decedent acquired property
                </li>
              </ul>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
              <h4 className="font-bold text-gray-900 mb-3 flex items-center">
                <i className="ri-file-list-3-line text-teal-600 mr-2"></i>
                Additional Statements
              </h4>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-line text-teal-600 mr-2 mt-0.5"></i>
                  Statement that no will exists (or was probated)
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-line text-teal-600 mr-2 mt-0.5"></i>
                  Statement about debts and creditors
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-line text-teal-600 mr-2 mt-0.5"></i>
                  Statement that no probate is pending
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-line text-teal-600 mr-2 mt-0.5"></i>
                  Affiant&apos;s relationship to deceased
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-lg mb-6">
            <h4 className="text-xl font-bold text-gray-900 mb-3">
              <i className="ri-error-warning-line mr-2 text-amber-500"></i>
              Important: The Disinterested Witness Requirement
            </h4>
            <p className="text-gray-700">
              Texas law requires that an Affidavit of Heirship be signed by a
              &quot;disinterested witness&quot; — someone who has no financial
              interest in the estate. This person must have personal knowledge
              of the deceased&apos;s family history. Finding the right person to
              serve as affiant is often the biggest challenge in preparing
              these documents. Many title companies also require a second
              disinterested witness.
            </p>
          </div>
        </section>

        {/* How to Get an Affidavit of Heirship Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            How to Get an Affidavit of Heirship Prepared
          </h2>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Getting an Affidavit of Heirship prepared involves several steps.
            Here&apos;s the typical process:
          </p>

          <div className="space-y-6 mb-8">
            <div className="flex items-start">
              <div className="w-12 h-12 flex items-center justify-center bg-teal-600 rounded-full mr-4 flex-shrink-0">
                <span className="text-white font-bold">1</span>
              </div>
              <div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">
                  Gather Family Information
                </h4>
                <p className="text-gray-700">
                  Collect all relevant information about the deceased and their
                  family. This includes birth certificates, death certificates,
                  marriage certificates, divorce decrees, and any other
                  documents that establish family relationships. The more
                  documentation you have, the stronger your affidavit will be.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-12 h-12 flex items-center justify-center bg-teal-600 rounded-full mr-4 flex-shrink-0">
                <span className="text-white font-bold">2</span>
              </div>
              <div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">
                  Find a Disinterested Witness
                </h4>
                <p className="text-gray-700">
                  Identify someone who knew the deceased and their family well
                  but who is NOT an heir. This could be a longtime neighbor,
                  family friend, church member, or distant relative who won&apos;t
                  inherit anything. This person must be willing to sign a sworn
                  statement about the family history.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-12 h-12 flex items-center justify-center bg-teal-600 rounded-full mr-4 flex-shrink-0">
                <span className="text-white font-bold">3</span>
              </div>
              <div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">
                  Have the Document Prepared
                </h4>
                <p className="text-gray-700">
                  While templates exist online, we strongly recommend having an
                  attorney prepare your Affidavit of Heirship. An improperly
                  drafted affidavit may be rejected by title companies, wasting
                  time and money. An experienced attorney knows exactly what
                  information title companies require and how to present it.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-12 h-12 flex items-center justify-center bg-teal-600 rounded-full mr-4 flex-shrink-0">
                <span className="text-white font-bold">4</span>
              </div>
              <div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">
                  Sign Before a Notary
                </h4>
                <p className="text-gray-700">
                  The affidavit must be signed by the disinterested
                  witness(es) in front of a notary public. The notary verifies
                  the signer&apos;s identity and administers an oath that the
                  statements in the affidavit are true. This notarization is
                  essential for the document to be legally valid.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-12 h-12 flex items-center justify-center bg-teal-600 rounded-full mr-4 flex-shrink-0">
                <span className="text-white font-bold">5</span>
              </div>
              <div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">
                  Record in County Records
                </h4>
                <p className="text-gray-700">
                  Once signed and notarized, the Affidavit of Heirship should be
                  recorded with the County Clerk in the county where the
                  property is located. In El Paso, this is the El Paso County
                  Clerk&apos;s Office. Recording creates a public record that
                  becomes part of the property&apos;s chain of title.
                </p>
              </div>
            </div>
          </div>

          <img
            src="https://readdy.ai/api/search-image?query=notary%20public%20stamping%20legal%20document%20with%20official%20seal%20professional%20office%20setting%20with%20legal%20paperwork%20and%20identification%20documents%20on%20desk%20warm%20lighting%20showing%20official%20document%20authentication%20process&width=800&height=500&seq=heirship-notary-004&orientation=landscape"
            alt="Notary public authenticating legal documents"
            className="w-full h-96 object-cover rounded-lg shadow-lg mb-8"
          />
        </section>

        {/* Middle Lead Capture Form */}
        <section className="mb-16">
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div className="text-white">
                <h2 className="text-3xl font-bold mb-4">
                  Need Help With Inherited Property?
                </h2>
                <p className="text-gray-300 mb-6">
                  Whether you need an Affidavit of Heirship or want to sell
                  inherited property quickly, we can help. We work with families
                  throughout El Paso to navigate the complexities of inherited
                  real estate.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center">
                    <i className="ri-check-line text-teal-400 mr-3"></i>
                    <span>Connect you with experienced attorneys</span>
                  </li>
                  <li className="flex items-center">
                    <i className="ri-check-line text-teal-400 mr-3"></i>
                    <span>Buy properties with heirship issues</span>
                  </li>
                  <li className="flex items-center">
                    <i className="ri-check-line text-teal-400 mr-3"></i>
                    <span>Work with multiple heirs</span>
                  </li>
                  <li className="flex items-center">
                    <i className="ri-check-line text-teal-400 mr-3"></i>
                    <span>Close on your timeline</span>
                  </li>
                </ul>
              </div>
              <div>
                <HeirshipContactForm
                  formId="middle-heirship-form"
                  title="Get Your Free Consultation"
                  subtitle="No obligation, no pressure"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Costs Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            How Much Does an Affidavit of Heirship Cost?
          </h2>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            One of the biggest advantages of an Affidavit of Heirship is its
            relatively low cost compared to formal probate. Here&apos;s what
            El Paso families can typically expect to pay:
          </p>

          <div className="overflow-x-auto mb-8">
            <table className="w-full border-collapse bg-white rounded-lg overflow-hidden shadow-lg">
              <thead>
                <tr className="bg-gray-900 text-white">
                  <th className="px-6 py-4 text-left font-semibold">
                    Cost Component
                  </th>
                  <th className="px-6 py-4 text-left font-semibold">
                    Typical Range
                  </th>
                  <th className="px-6 py-4 text-left font-semibold">Notes</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-200">
                  <td className="px-6 py-4 font-medium">
                    Attorney Preparation
                  </td>
                  <td className="px-6 py-4">$300 - $800</td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    Varies by complexity
                  </td>
                </tr>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <td className="px-6 py-4 font-medium">Notary Fees</td>
                  <td className="px-6 py-4">$10 - $50</td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    Per signature
                  </td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="px-6 py-4 font-medium">Recording Fees</td>
                  <td className="px-6 py-4">$25 - $75</td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    El Paso County Clerk
                  </td>
                </tr>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <td className="px-6 py-4 font-medium">Document Copies</td>
                  <td className="px-6 py-4">$5 - $25</td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    Certified copies
                  </td>
                </tr>
                <tr className="bg-teal-50">
                  <td className="px-6 py-4 font-bold">
                    Total Estimated Cost
                  </td>
                  <td className="px-6 py-4 font-bold text-teal-600">
                    $350 - $950
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    Much less than probate
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Compare this to formal probate, which typically costs $2,000 to
            $10,000 or more in attorney fees alone, plus court costs, executor
            fees, and other expenses. For many families, the cost savings make
            an Affidavit of Heirship the obvious choice.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            However, cost shouldn&apos;t be the only factor. If there are disputes
            among heirs, significant debts, or complex family situations,
            formal probate may provide better protection. Understanding{' '}
            <a
              href="/elpaso-tx/texas-intestacy-laws"
              className="text-teal-600 hover:text-teal-700 font-medium"
            >
              how Texas intestacy laws determine inheritance
            </a>{' '}
            can help you assess whether an Affidavit of Heirship is appropriate
            for your situation.
          </p>
        </section>

        {/* Limitations Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Limitations of an Affidavit of Heirship
          </h2>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            While Affidavits of Heirship are incredibly useful, they&apos;re not
            perfect solutions for every situation. Understanding their
            limitations helps you make informed decisions:
          </p>

          <div className="space-y-6 mb-8">
            <div className="bg-red-50 p-5 rounded-lg border-l-4 border-red-500">
              <h4 className="font-bold text-gray-900 mb-2">
                <i className="ri-close-circle-line text-red-500 mr-2"></i>
                Not a Court Order
              </h4>
              <p className="text-gray-700 text-sm">
                An Affidavit of Heirship is not a court decree. It doesn&apos;t
                have the same legal authority as a probate order. While most
                title companies accept them, some may require additional
                documentation or refuse to insure title based solely on an
                affidavit, especially for high-value properties.
              </p>
            </div>

            <div className="bg-red-50 p-5 rounded-lg border-l-4 border-red-500">
              <h4 className="font-bold text-gray-900 mb-2">
                <i className="ri-close-circle-line text-red-500 mr-2"></i>
                Doesn&apos;t Resolve Disputes
              </h4>
              <p className="text-gray-700 text-sm">
                If heirs disagree about who should inherit or how property
                should be divided, an Affidavit of Heirship won&apos;t resolve
                those disputes. You&apos;ll need court intervention — either
                through probate or a partition lawsuit — to settle disagreements.
              </p>
            </div>

            <div className="bg-red-50 p-5 rounded-lg border-l-4 border-red-500">
              <h4 className="font-bold text-gray-900 mb-2">
                <i className="ri-close-circle-line text-red-500 mr-2"></i>
                Doesn&apos;t Address Debts
              </h4>
              <p className="text-gray-700 text-sm">
                An Affidavit of Heirship doesn&apos;t pay or discharge the
                deceased&apos;s debts. If there are outstanding liens,
                mortgages, or other debts against the property, those must still
                be addressed. Creditors may still have claims against the estate.
              </p>
            </div>

            <div className="bg-red-50 p-5 rounded-lg border-l-4 border-red-500">
              <h4 className="font-bold text-gray-900 mb-2">
                <i className="ri-close-circle-line text-red-500 mr-2"></i>
                Only Works for Real Property
              </h4>
              <p className="text-gray-700 text-sm">
                Affidavits of Heirship are primarily used for real estate.
                They generally won&apos;t help you transfer bank accounts,
                vehicles, or other personal property. Those assets may require
                other procedures like Small Estate Affidavits or formal probate.
              </p>
            </div>

            <div className="bg-red-50 p-5 rounded-lg border-l-4 border-red-500">
              <h4 className="font-bold text-gray-900 mb-2">
                <i className="ri-close-circle-line text-red-500 mr-2"></i>
                Waiting Period May Apply
              </h4>
              <p className="text-gray-700 text-sm">
                Some title companies require that an Affidavit of Heirship be
                on record for a certain period (often 5 years) before they&apos;ll
                insure title without additional requirements. This &quot;seasoning&quot;
                period can delay sales if the affidavit was recently recorded.
              </p>
            </div>
          </div>

          <img
            src="https://readdy.ai/api/search-image?query=concerned%20family%20members%20in%20discussion%20around%20table%20with%20legal%20documents%20showing%20disagreement%20or%20confusion%20about%20inheritance%20matters%20warm%20home%20setting%20natural%20lighting%20professional%20photography&width=800&height=500&seq=heirship-limitations-005&orientation=landscape"
            alt="Family discussing inheritance complications"
            className="w-full h-96 object-cover rounded-lg shadow-lg mb-8"
          />
        </section>

        {/* Affidavit vs Other Options Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Affidavit of Heirship vs. Other Options
          </h2>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            How does an Affidavit of Heirship compare to other ways of
            transferring inherited property? Here&apos;s a quick comparison:
          </p>

          <div className="overflow-x-auto mb-8">
            <table className="w-full border-collapse bg-white rounded-lg overflow-hidden shadow-lg text-sm">
              <thead>
                <tr className="bg-gray-900 text-white">
                  <th className="px-4 py-3 text-left font-semibold">Option</th>
                  <th className="px-4 py-3 text-left font-semibold">Best For</th>
                  <th className="px-4 py-3 text-left font-semibold">Cost</th>
                  <th className="px-4 py-3 text-left font-semibold">Timeline</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-200">
                  <td className="px-4 py-3 font-medium">Affidavit of Heirship</td>
                  <td className="px-4 py-3">
                    No will, simple family, real estate only
                  </td>
                  <td className="px-4 py-3">$350-$950</td>
                  <td className="px-4 py-3">1-4 weeks</td>
                </tr>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <td className="px-4 py-3 font-medium">Small Estate Affidavit</td>
                  <td className="px-4 py-3">Estates under $75,000</td>
                  <td className="px-4 py-3">$200-$500</td>
                  <td className="px-4 py-3">30+ days after death</td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="px-4 py-3 font-medium">Muniment of Title</td>
                  <td className="px-4 py-3">Valid will, no debts, real estate</td>
                  <td className="px-4 py-3">$1,000-$2,500</td>
                  <td className="px-4 py-3">4-8 weeks</td>
                </tr>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <td className="px-4 py-3 font-medium">Independent Probate</td>
                  <td className="px-4 py-3">Larger estates, complex situations</td>
                  <td className="px-4 py-3">$2,000-$5,000+</td>
                  <td className="px-4 py-3">3-12 months</td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="px-4 py-3 font-medium">Dependent Probate</td>
                  <td className="px-4 py-3">Disputes, court oversight needed</td>
                  <td className="px-4 py-3">$5,000-$15,000+</td>
                  <td className="px-4 py-3">1-3+ years</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            The right choice depends on your specific circumstances. If you&apos;re
            unsure which option is best, consulting with a probate attorney can
            help you understand your options. For a deeper dive into the probate
            process, see our{' '}
            <a
              href="/land-buyers"
              className="text-teal-600 hover:text-teal-700 font-medium"
            >
              guide to selling inherited land in Texas
            </a>
            , which covers many of the same considerations.
          </p>
        </section>

        {/* Selling Property Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Selling Property After Recording an Affidavit of Heirship
          </h2>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Once your Affidavit of Heirship is recorded, you&apos;re one step
            closer to being able to sell the inherited property. However, there
            are still some important considerations:
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            Getting All Heirs to Agree
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            If there are multiple heirs, ALL of them must agree to sell and sign
            the deed. This can be challenging if heirs are scattered across the
            country, have different opinions about selling, or can&apos;t be
            located. One uncooperative heir can block the entire sale.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            If you&apos;re dealing with a situation where{' '}
            <a
              href="/sell-apartments"
              className="text-teal-600 hover:text-teal-700 font-medium"
            >
              multiple family members own shares of an apartment building
            </a>{' '}
            or other property, getting everyone aligned can be especially
            complex.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            Title Insurance Considerations
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Title companies have varying policies about insuring property
            transferred via Affidavit of Heirship. Some will insure immediately;
            others want the affidavit to be &quot;seasoned&quot; (on record for
            several years). Some may require additional documentation or
            affidavits from the heirs themselves.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Working with a buyer who understands these complexities — like a
            cash home buyer experienced with inherited properties — can make the
            process much smoother. Traditional buyers with conventional financing
            may face more obstacles.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            Dealing with Property Condition Issues
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Inherited properties often need work. Maybe the deceased couldn&apos;t
            maintain the home in their final years, or the property has sat
            vacant. If you&apos;re dealing with a property that needs significant
            repairs or has been neglected, selling to a{' '}
            <a
              href="/elpaso-tx/cash-home-buyers-el-paso"
              className="text-teal-600 hover:text-teal-700 font-medium"
            >
              cash home buyer in El Paso
            </a>{' '}
            who purchases as-is can eliminate the need for costly renovations.
          </p>

          <img
            src="https://readdy.ai/api/search-image?query=real%20estate%20closing%20table%20with%20documents%20being%20signed%20by%20multiple%20people%20hands%20visible%20showing%20property%20sale%20transaction%20professional%20office%20setting%20warm%20lighting%20showing%20successful%20real%20estate%20deal&width=800&height=500&seq=heirship-closing-006&orientation=landscape"
            alt="Property sale closing with multiple parties signing"
            className="w-full h-96 object-cover rounded-lg shadow-lg mb-8"
          />
        </section>

        {/* Common Questions Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Frequently Asked Questions About Affidavits of Heirship
          </h2>

          <div className="space-y-6">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                How long does an Affidavit of Heirship take to prepare?
              </h3>
              <p className="text-gray-700">
                Once you have all the necessary information and have identified a
                disinterested witness, an attorney can typically prepare the
                document within a few days to a week. The signing and recording
                process adds another few days. Overall, you can usually complete
                the entire process in 2-4 weeks.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Can I prepare an Affidavit of Heirship myself?
              </h3>
              <p className="text-gray-700">
                While templates exist online, we strongly recommend using an
                attorney. An improperly prepared affidavit may be rejected by
                title companies, and errors can create legal problems down the
                road. The cost of professional preparation is minimal compared to
                the potential complications of a DIY approach.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                What if I can&apos;t find a disinterested witness?
              </h3>
              <p className="text-gray-700">
                This is one of the most common challenges. If you truly cannot
                find someone who knew the deceased and isn&apos;t an heir, you
                may need to pursue other options like formal probate or a
                Determination of Heirship proceeding in court. An attorney can
                help you explore alternatives.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Does an Affidavit of Heirship expire?
              </h3>
              <p className="text-gray-700">
                No, an Affidavit of Heirship doesn&apos;t expire. Once recorded,
                it becomes a permanent part of the property&apos;s chain of
                title. In fact, the longer it&apos;s been on record without
                challenge, the stronger it becomes as evidence of heirship.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Can an Affidavit of Heirship be challenged?
              </h3>
              <p className="text-gray-700">
                Yes, anyone with a claim to the estate can challenge an Affidavit
                of Heirship. If someone believes they were wrongly excluded as an
                heir, or that the information in the affidavit is incorrect, they
                can file a lawsuit. This is why accuracy is so important when
                preparing these documents.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                What if the deceased owned property in multiple counties?
              </h3>
              <p className="text-gray-700">
                You&apos;ll need to record the Affidavit of Heirship in each
                county where the deceased owned real property. The same affidavit
                can be used, but it must be recorded separately in each
                county&apos;s property records.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Can I use an Affidavit of Heirship if there was a will?
              </h3>
              <p className="text-gray-700">
                Generally, no. If there was a valid will, the proper procedure is
                usually to probate the will (even if simplified procedures like
                Muniment of Title). An Affidavit of Heirship is specifically
                designed for intestate situations where no will exists or was
                probated.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                What happens if an heir has also died?
              </h3>
              <p className="text-gray-700">
                If an heir died after the original property owner, their share
                typically passes to their own heirs. This can create complex
                &quot;chain of heirship&quot; situations requiring multiple
                affidavits. If an heir died before the property owner, their
                share may pass to their children (the original owner&apos;s
                grandchildren) under Texas intestacy laws.
              </p>
            </div>
          </div>
        </section>

        {/* How We Can Help Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            How TREX RE LLC Can Help
          </h2>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            At TREX RE LLC, we specialize in helping El Paso families navigate
            the complexities of inherited property. We understand that dealing
            with legal documents, multiple heirs, and property issues while
            grieving is overwhelming. That&apos;s why we offer solutions that
            simplify the process:
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-teal-50 p-6 rounded-lg">
              <div className="w-12 h-12 flex items-center justify-center bg-teal-600 rounded-full mb-4">
                <i className="ri-team-line text-white text-xl"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-2">
                Attorney Referrals
              </h4>
              <p className="text-gray-700">
                We work with experienced probate attorneys throughout El Paso and
                can connect you with professionals who specialize in Affidavits
                of Heirship and inherited property issues.
              </p>
            </div>

            <div className="bg-teal-50 p-6 rounded-lg">
              <div className="w-12 h-12 flex items-center justify-center bg-teal-600 rounded-full mb-4">
                <i className="ri-money-dollar-circle-line text-white text-xl"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-2">
                Cash Offers for Inherited Property
              </h4>
              <p className="text-gray-700">
                Once your heirship documentation is in order, we can make a fair
                cash offer for the property. We buy houses in any condition, so
                you don&apos;t need to make repairs or clean out the home.
              </p>
            </div>

            <div className="bg-teal-50 p-6 rounded-lg">
              <div className="w-12 h-12 flex items-center justify-center bg-teal-600 rounded-full mb-4">
                <i className="ri-group-line text-white text-xl"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-2">
                Multiple Heir Coordination
              </h4>
              <p className="text-gray-700">
                We have experience working with multiple heirs, even when they&apos;
                re located in different states. We can help coordinate the sale
                process to ensure everyone gets their fair share.
              </p>
            </div>

            <div className="bg-teal-50 p-6 rounded-lg">
              <div className="w-12 h-12 flex items-center justify-center bg-teal-600 rounded-full mb-4">
                <i className="ri-time-line text-white text-xl"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-2">
                Flexible Timelines
              </h4>
              <p className="text-gray-700">
                Whether you need to close quickly or need time to sort out
                documentation, we work on your schedule. We understand that
                inherited property situations don&apos; t always fit neat
                timelines.
              </p>
            </div>
          </div>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            If you&apos;re dealing with property that has{' '}
            <a
              href="/title-issues"
              className="text-teal-600 hover:text-teal-700 font-medium"
            >
              title complications beyond heirship issues
            </a>
            , we can often still help. We&apos;ve purchased properties with
            liens, boundary disputes, and other title problems that would scare
            away traditional buyers.
          </p>
        </section>

        {/* Final CTA Section */}
        <section className="mb-16">
          <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-2xl p-8 md:p-12 text-white">
            <div className="text-center mb-8">
              <div className="w-20 h-20 flex items-center justify-center bg-white/20 rounded-full mx-auto mb-6">
                <i className="ri-hand-heart-line text-4xl"></i>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Ready to Move Forward With Your Inherited Property?
              </h2>
              <p className="text-xl text-teal-100 max-w-2xl mx-auto">
                Whether you need help understanding your options, finding an
                attorney to prepare an Affidavit of Heirship, or selling
                inherited property quickly, we&apos;re here to help El Paso
                families every step of the way.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8">
              <HeirshipContactForm
                formId="final-heirship-form"
                title="Get Your Free Consultation"
                subtitle="No obligation, compassionate service"
              />
            </div>

            <div className="mt-8 text-center">
              <p className="text-teal-100 mb-4">Prefer to talk? Call us directly:</p>
              <a
                href="tel:+1-915-206-5814"
                className="inline-flex items-center px-8 py-4 bg-white text-teal-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap text-lg"
              >
                <i className="ri-phone-line mr-2"></i>(915) 206-5814
              </a>
            </div>
          </div>
        </section>

        {/* Related Resources */}
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Related Resources
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            <a
              href="/elpaso-tx/texas-probate-guide"
              className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <i className="ri-scales-3-line text-teal-600 text-2xl mr-4"></i>
              <div>
                <p className="font-semibold text-gray-900">
                  Texas Probate Guide
                </p>
                <p className="text-sm text-gray-600">
                  Complete probate process explained
                </p>
              </div>
            </a>
            <a
              href="/elpaso-tx/texas-intestacy-laws"
              className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <i className="ri-file-list-3-line text-teal-600 text-2xl mr-4"></i>
              <div>
                <p className="font-semibold text-gray-900">
                  Texas Intestacy Laws
                </p>
                <p className="text-sm text-gray-600">
                  Who inherits when there&apos;s no will
                </p>
              </div>
            </a>
            <a
              href="/inherited-homes"
              className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <i className="ri-home-heart-line text-teal-600 text-2xl mr-4"></i>
              <div>
                <p className="font-semibold text-gray-900">
                  Selling Inherited Homes
                </p>
                <p className="text-sm text-gray-600">
                  Options for inherited property
                </p>
              </div>
            </a>
            <a
              href="/get-cash-offer-for-my-property"
              className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <i className="ri-money-dollar-circle-line text-teal-600 text-2xl mr-4"></i>
              <div>
                <p className="font-semibold text-gray-900">Get Cash Offer</p>
                <p className="text-sm text-gray-600">
                  Request your no-obligation offer
                </p>
              </div>
            </a>
          </div>
        </section>
      </article>

      <Footer />
    </div>
  );
}
